import { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import Swal from 'sweetalert2';
import { IContaBancaria, IExtratoModulo, IModuloBancarioVisao, ISugestaoContaBancaria } from '../../interfaces/IModuloBancario';
import ArquivoOperacaoService from '../../services/ArquivoOperacaoService';
import ExtratoBancarioService from '../../services/ExtratoBancarioService';
import ModuloBancarioService from '../../services/ModuloBancarioService';
import { descreverConta } from './contas-bancarias';
import { BarraFiltros, PaginacaoModulo, useFiltroLista } from './paginacao-modulo';

const INTERVALO_ATUALIZACAO_MS = 4000;

const SITUACOES_FILTRO: Record<string, string> = {
    AGUARDANDO_CONTA: 'Escolha a conta',
    AGUARDANDO_SALDO: 'Confirme o saldo',
    PRONTO: 'Pronto para processar',
    ACEITO: 'Aceito',
    RETIDO: 'Retido',
};

function brl(valor: number | null | undefined): string {
    if (valor === null || valor === undefined) return '—';
    return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function data(valor: string | null | undefined): string {
    return valor ? moment(valor).format('DD/MM/YYYY') : '—';
}

/** "1.234,56" ou "1234.56" → número. */
function lerValor(texto: string): number | null {
    const limpo = texto.trim();
    if (!limpo) return null;
    const normalizado = limpo.includes(',') ? limpo.replace(/\./g, '').replace(',', '.') : limpo;
    const n = Number(normalizado);
    return isNaN(n) ? null : n;
}

type Situacao = { label: string; cor: string };

function situacao(e: IExtratoModulo): Situacao {
    if (e.arquivoProcessando) return { label: 'Processando', cor: '#0d6efd' };
    const pdf = e.pdf?.status;
    if (pdf === 'ANALISANDO') return { label: 'Calculando custo', cor: '#6c757d' };
    if (pdf === 'AGUARDANDO_DECISAO') return { label: 'Aguardando decisão (custo)', cor: '#f1bc00' };
    if (pdf === 'EXTRAINDO') return { label: 'Lendo PDF', cor: '#0d6efd' };
    if (pdf === 'AGUARDANDO_CONFIRMACAO' && e.conta.situacao !== 'AGUARDANDO_SALDO')
        return { label: 'Leitura não fechou', cor: '#f1bc00' };

    switch (e.conta.situacao) {
        case 'AGUARDANDO_CONTA': return { label: 'Escolha a conta', cor: '#f1bc00' };
        case 'AGUARDANDO_SALDO': return { label: 'Confirme o saldo', cor: '#f1bc00' };
        case 'PRONTO': return { label: 'Pronto para processar', cor: '#6c757d' };
        case 'ACEITO': return { label: e.conta.primeiraImportacao ? 'Aceito (primeira importação)' : 'Aceito', cor: '#50cd89' };
        case 'RETIDO': return { label: 'Retido', cor: '#f1416c' };
        default: return { label: e.conta.situacao, cor: '#6c757d' };
    }
}

const VAZIO: IModuloBancarioVisao = { contas: [], extratos: [], totalExtratos: 0, pagina: 1, tamanhoPagina: 50, prontos: 0, emAndamento: false };

interface Props {
    esteiraId: number;
    competencia: string;
    contas: IContaBancaria[];
    /** Muda quando as contas ou o fechamento mudam. */
    versao: number;
    /** Enviar, processar ou descartar muda as contagens das etapas. */
    onAlterou: () => void;
    onCadastrarConta: (sugestao: { arquivoId: number; dados: ISugestaoContaBancaria }) => void;
}

// DEVN3-767 — extratos com continuidade de saldo por conta. A conciliação razão × extrato continua sendo a do cartão.
export default function ExtratosBancarios({ esteiraId, competencia, contas, versao, onAlterou, onCadastrarConta }: Props) {
    const [visao, setVisao] = useState<IModuloBancarioVisao>(VAZIO);
    const [carregando, setCarregando] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [iniciando, setIniciando] = useState(false);
    const [contaUpload, setContaUpload] = useState<number>(0);
    const [contaFiltro, setContaFiltro] = useState<number>(0);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const filtro = useFiltroLista();
    const { setPagina } = filtro;

    const contasAtivas = contas.filter(c => c.ativo);

    const buscar = useCallback(async (silencioso = false) => {
        if (!silencioso) setCarregando(true);
        try {
            const [ano, mes] = competencia ? competencia.split('-').map(Number) : [undefined, undefined];
            setVisao(await ModuloBancarioService.listar(esteiraId, ano, mes, { ...filtro.params, contaBancariaId: contaFiltro || undefined }));
        } catch (err: any) {
            if (!silencioso) Swal.fire({ text: err?.message ?? err?.response?.data?.message ?? 'Não foi possível carregar os extratos.', icon: 'error' });
        } finally {
            if (!silencioso) setCarregando(false);
        }
    }, [esteiraId, competencia, filtro.params, contaFiltro]);

    useEffect(() => { buscar(); }, [buscar]);

    useEffect(() => {
        if (versao > 0) buscar(true);
    }, [versao, buscar]);

    useEffect(() => {
        if (!visao.emAndamento) return;
        const id = setInterval(() => buscar(true), INTERVALO_ATUALIZACAO_MS);
        return () => clearInterval(id);
    }, [visao.emAndamento, buscar]);

    // Terminado o processamento, a etapa e as minutas precisam acompanhar.
    const processavaAntes = useRef(false);
    useEffect(() => {
        if (processavaAntes.current && !visao.emAndamento) onAlterou();
        processavaAntes.current = visao.emAndamento;
    }, [visao.emAndamento, onAlterou]);

    const executar = async (acao: () => Promise<unknown>, sucesso: string) => {
        try {
            await acao();
            Swal.fire({ text: sucesso, icon: 'success', timer: 2000, showConfirmButton: false });
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? err?.response?.data?.message ?? 'Não foi possível concluir a ação.', icon: 'error' });
        }
        buscar(true);
        onAlterou();
    };

    const enviar = async (ev: React.ChangeEvent<HTMLInputElement>) => {
        const files = ev.target.files;
        if (!files || files.length === 0) return;

        setEnviando(true);
        const falhas: string[] = [];
        for (const file of Array.from(files)) {
            try {
                if (file.name.toLowerCase().endsWith('.pdf') && !contaUpload)
                    throw new Error('Escolha a conta bancária antes de enviar um PDF.');
                const { signedUrl, generatedFileName } = await ArquivoOperacaoService.gerarUrl(file.name, file.type || 'application/octet-stream');
                await ArquivoOperacaoService.enviarParaStorage(signedUrl, file);
                await ModuloBancarioService.registrarExtrato(esteiraId, generatedFileName, file.name, contaUpload || undefined);
            } catch (err: any) {
                falhas.push(`${file.name}: ${err?.message ?? err?.response?.data?.message ?? err?.message ?? 'falha no envio'}`);
            }
        }
        setEnviando(false);
        if (fileInputRef.current) fileInputRef.current.value = '';

        if (falhas.length > 0) Swal.fire({ title: 'Alguns arquivos não foram enviados', html: falhas.join('<br/>'), icon: 'warning' });
        else Swal.fire({ text: 'Extrato(s) enviado(s). Confira a situação de cada um antes de processar.', icon: 'success', timer: 2500, showConfirmButton: false });
        buscar();
        onAlterou();
    };

    const iniciar = async () => {
        setIniciando(true);
        try {
            const mensagem = await ModuloBancarioService.iniciar(esteiraId);
            Swal.fire({ text: mensagem, icon: 'success', timer: 2500, showConfirmButton: false });
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? err?.response?.data?.message ?? 'Não foi possível iniciar o processamento.', icon: 'error' });
        } finally {
            setIniciando(false);
            buscar(true);
            onAlterou();
        }
    };

    const escolherConta = async (e: IExtratoModulo) => {
        const opcoes: Record<string, string> = {};
        contasAtivas.forEach(c => { opcoes[String(c.id)] = descreverConta(c); });
        const { value } = await Swal.fire({
            title: 'Conta bancária do extrato',
            text: e.conta.contaDetectada ? `O arquivo traz: ${e.conta.contaDetectada}` : undefined,
            input: 'select',
            inputOptions: opcoes,
            inputPlaceholder: 'Selecione',
            showCancelButton: true,
            confirmButtonText: 'Vincular',
            cancelButtonText: 'Cancelar',
        });
        if (value) executar(() => ModuloBancarioService.vincularConta(e.arquivoOperacaoId, Number(value)), 'Conta vinculada.');
    };

    const cadastrarPelaSugestao = async (e: IExtratoModulo) => {
        try {
            const dados = await ModuloBancarioService.sugestao(e.arquivoOperacaoId);
            if (!dados) {
                Swal.fire({ text: 'O arquivo não traz banco e conta, ou a conta já está cadastrada. Use "Escolher conta".', icon: 'info' });
                return;
            }
            onCadastrarConta({ arquivoId: e.arquivoOperacaoId, dados });
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? err?.response?.data?.message ?? 'Não foi possível ler o arquivo.', icon: 'error' });
        }
    };

    const informarSaldos = async (e: IExtratoModulo) => {
        const inicial = e.conta.saldoInicial?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) ?? '';
        const final = e.conta.saldoFinal?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) ?? '';
        const { value } = await Swal.fire({
            title: 'Saldos do extrato',
            html: `
                <p class="text-muted fs-7 mb-4">O arquivo não traz saldo confiável. Confira no extrato do banco.
                Entradas ${brl(e.conta.totalEntradas)} · Saídas ${brl(e.conta.totalSaidas)}</p>
                <label class="form-label">Saldo inicial</label>
                <input id="mb-saldo-inicial" class="form-control mb-3" value="${inicial}">
                <label class="form-label">Saldo final</label>
                <input id="mb-saldo-final" class="form-control" value="${final}">`,
            showCancelButton: true,
            confirmButtonText: 'Confirmar saldos',
            cancelButtonText: 'Cancelar',
            preConfirm: () => {
                const si = lerValor((document.getElementById('mb-saldo-inicial') as HTMLInputElement).value);
                const sf = lerValor((document.getElementById('mb-saldo-final') as HTMLInputElement).value);
                if (si === null || sf === null) {
                    Swal.showValidationMessage('Informe os dois saldos.');
                    return false;
                }
                return { si, sf };
            },
        });
        if (value) executar(() => ModuloBancarioService.informarSaldos(e.arquivoOperacaoId, value.si, value.sf), 'Saldos registrados.');
    };

    const descartar = async (e: IExtratoModulo) => {
        const { isConfirmed } = await Swal.fire({
            text: `Descartar o extrato ${e.nomeOriginal}? Ele sai da fila e nenhuma linha é gravada.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Descartar',
            cancelButtonText: 'Cancelar',
        });
        if (isConfirmed) executar(() => ModuloBancarioService.descartar(e.arquivoOperacaoId), 'Extrato descartado.');
    };

    const acoes = (e: IExtratoModulo) => {
        if (e.arquivoProcessando) return null;
        const pdf = e.pdf?.status;
        const botoes: JSX.Element[] = [];

        if (pdf === 'AGUARDANDO_DECISAO') {
            botoes.push(<button key='proc' className='btn btn-sm btn-light-primary' onClick={() => executar(() => ExtratoBancarioService.decidirPdf(e.arquivoOperacaoId, true), 'PDF liberado para processamento.')}>Processar</button>);
        }
        if (pdf === 'AGUARDANDO_CONFIRMACAO' && e.conta.situacao !== 'AGUARDANDO_SALDO' && e.conta.origemSaldo !== 'USUARIO') {
            botoes.push(<button key='imp' className='btn btn-sm btn-light-primary' onClick={() => executar(() => ExtratoBancarioService.confirmarPdf(e.arquivoOperacaoId, 'IMPORTAR'), 'Importação confirmada.')}>Importar mesmo assim</button>);
        }
        if (pdf === 'AGUARDANDO_CONFIRMACAO' && e.conta.situacao === 'PRONTO' && e.conta.origemSaldo === 'USUARIO') {
            botoes.push(<button key='conf' className='btn btn-sm btn-light-primary' onClick={() => executar(() => ExtratoBancarioService.confirmarPdf(e.arquivoOperacaoId, 'IMPORTAR'), 'Importação confirmada.')}>Importar com os saldos informados</button>);
        }
        if (e.conta.situacao === 'AGUARDANDO_CONTA') {
            botoes.push(<button key='esc' className='btn btn-sm btn-light-primary' onClick={() => escolherConta(e)} disabled={contasAtivas.length === 0}>Escolher conta</button>);
            if (!e.nomeOriginal.toLowerCase().endsWith('.pdf'))
                botoes.push(<button key='cad' className='btn btn-sm btn-light' onClick={() => cadastrarPelaSugestao(e)}>Cadastrar conta</button>);
        }
        if (e.conta.situacao === 'AGUARDANDO_SALDO') {
            botoes.push(<button key='sal' className='btn btn-sm btn-light-primary' onClick={() => informarSaldos(e)}>Informar saldos</button>);
        }
        if (['AGUARDANDO_CONTA', 'AGUARDANDO_SALDO', 'PRONTO', 'RETIDO'].includes(e.conta.situacao)) {
            botoes.push(<button key='desc' className='btn btn-sm btn-light-danger' onClick={() => descartar(e)}>Descartar</button>);
        }
        return <div className='d-flex flex-wrap gap-1 justify-content-end'>{botoes}</div>;
    };

    const prontos = visao.prontos;
    const filtrando = filtro.ativo || contaFiltro > 0;

    return (
        <div className='card card-flush mb-4'>
            <div className='card-header align-items-center'>
                <h3 className='card-title fw-bold'>Extratos</h3>
                <div className='d-flex flex-wrap align-items-center gap-2'>
                    <select className='form-select form-select-sm' style={{ width: 280 }} aria-label='Conta do envio' value={contaUpload}
                        onChange={ev => setContaUpload(Number(ev.target.value))}>
                        <option value={0}>Identificar a conta pelo arquivo (OFX)</option>
                        {contasAtivas.map(c => <option key={c.id} value={c.id}>{descreverConta(c)}</option>)}
                    </select>
                    <input ref={fileInputRef} type='file' accept='.ofx,.pdf' multiple className='d-none' onChange={enviar} />
                    <button className='btn btn-sm btn-light-primary' disabled={enviando} onClick={() => fileInputRef.current?.click()}>
                        {enviando ? <span className='spinner-border spinner-border-sm me-1'></span> : <i className='fas fa-upload me-1'></i>}
                        Enviar extratos
                    </button>
                    <button className='btn btn-sm btn-primary' disabled={iniciando || prontos === 0} onClick={iniciar}
                        title={prontos === 0 ? 'Nenhum extrato pronto para processar' : undefined}>
                        {iniciando ? <span className='spinner-border spinner-border-sm me-1'></span> : <i className='fas fa-play me-1'></i>}
                        Iniciar processamento{prontos > 0 ? ` (${prontos})` : ''}
                    </button>
                </div>
            </div>
            <div className='card-body pt-0'>
                <div className='text-muted fs-7 mb-3'>
                    O saldo inicial de cada extrato precisa ser igual ao saldo final do último extrato aceito da mesma conta bancária.
                    Extrato que não fecha fica retido e não é gravado.
                </div>
                {(visao.totalExtratos > 0 || filtrando) && (
                    <BarraFiltros filtro={filtro} placeholder='Buscar pelo nome do arquivo' situacoes={SITUACOES_FILTRO}>
                        <select className='form-select form-select-sm w-auto' aria-label='Conta bancária' value={contaFiltro}
                            onChange={ev => { setContaFiltro(Number(ev.target.value)); setPagina(1); }}>
                            <option value={0}>Todas as contas</option>
                            {contas.map(c => <option key={c.id} value={c.id}>{descreverConta(c)}</option>)}
                        </select>
                    </BarraFiltros>
                )}
                {carregando && visao.extratos.length === 0 ? (
                    <div className='d-flex align-items-center gap-2 text-muted py-4'>
                        <span className='spinner-border spinner-border-sm'></span>Carregando...
                    </div>
                ) : visao.extratos.length === 0 ? (
                    <div className='text-muted py-4'>
                        {filtrando ? 'Nenhum extrato com estes filtros.' : `Nenhum extrato ${competencia ? 'nesta competência' : 'neste período'}.`}
                    </div>
                ) : (
                    <div className='table-responsive'>
                        <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2'>
                            <thead>
                                <tr className='fw-bold text-muted'>
                                    <th>Arquivo</th>
                                    <th>Conta</th>
                                    <th>Período</th>
                                    <th className='text-end'>Saldo inicial</th>
                                    <th className='text-end'>Saldo final</th>
                                    <th>Situação</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {visao.extratos.map(e => {
                                    const s = situacao(e);
                                    return (
                                        <tr key={e.arquivoOperacaoId}>
                                            <td>
                                                <div className='fw-semibold'>{e.nomeOriginal}</div>
                                                <div className='text-muted fs-8'>{moment(e.dataCadastro).format('DD/MM/YYYY HH:mm')}</div>
                                            </td>
                                            <td>
                                                {e.conta.contaDescricao ?? (
                                                    <span className='text-muted'>{e.conta.contaDetectada ?? 'não identificada'}</span>
                                                )}
                                            </td>
                                            <td>{e.conta.periodoInicio ? `${data(e.conta.periodoInicio)} a ${data(e.conta.periodoFim)}` : '—'}</td>
                                            <td className='text-end'>{brl(e.conta.saldoInicial)}</td>
                                            <td className='text-end'>
                                                {brl(e.conta.saldoFinal)}
                                                {e.conta.origemSaldo === 'USUARIO' && <div className='text-muted fs-8'>informado pelo usuário</div>}
                                            </td>
                                            <td style={{ maxWidth: 360 }}>
                                                <span className='badge' style={{ color: s.cor, background: `${s.cor}22` }}>{s.label}</span>
                                                {e.conta.motivo && <div className='text-danger fs-8 mt-1'>{e.conta.motivo}</div>}
                                                {e.conta.aviso && <div className='text-warning fs-8 mt-1'>{e.conta.aviso}</div>}
                                                {e.pdf?.mensagem && e.pdf.status !== 'IMPORTADO' && <div className='text-muted fs-8 mt-1'>{e.pdf.mensagem}</div>}
                                            </td>
                                            <td>{acoes(e)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                <PaginacaoModulo total={visao.totalExtratos} pagina={filtro.pagina} tamanhoPagina={filtro.tamanhoPagina} onPagina={filtro.setPagina} onTamanho={filtro.setTamanho} />
            </div>
        </div>
    );
}
